import React from 'react';
import { HiOutlineCheck } from 'react-icons/hi';
import { formatPhoneMaskDigits4To7 } from './utils/formatUtils';

/** 원 사용자 기준 선택 가능한 대직 후보만 true (비매칭 행은 목록에서 제외) */
function isSubstituteRowSelectable(candidate, currentUser) {
  const p = candidate.permissions || [];
  const hasCandDesign = p.includes('DESIGN_RECEIVE_CREATE');
  const hasCandSub = p.includes('SUBSCRIPTION_RECEIVE_CREATE');
  if (!hasCandDesign && !hasCandSub) return false;

  const perms = currentUser?.permissions;
  if (!Array.isArray(perms) || perms.length === 0) {
    return true;
  }
  const vDes = perms.includes('DESIGN_RECEIVE_CREATE');
  const vSub = perms.includes('SUBSCRIPTION_RECEIVE_CREATE');
  if (!vDes && !vSub) {
    return hasCandDesign || hasCandSub;
  }
  return (vDes && hasCandDesign) || (vSub && hasCandSub);
}

const SubstituteUserSelection = ({
  visible,
  onClose,
  availableSubstituteUsers,
  selectedSubstituteUserId,
  onSelectSubstituteUser,
  currentUser,
  showAlert: _showAlert
}) => {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">대직 담당자 선택</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="overflow-y-auto px-6 py-4 flex-1">
          <p className="text-sm text-gray-600 mb-4">
            설계수신, 청약수신 권한이 있는 사용자만 선택 가능합니다.
          </p>
          {availableSubstituteUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              대직 가능한 사용자가 없습니다.
            </div>
          ) : (
            <div className="space-y-4">
              {/* 대직 담당자 없음 옵션 */}
              <div
                onClick={() => {
                  onSelectSubstituteUser(null);
                  onClose();
                }}
                className={`px-4 py-3 border-2 rounded-lg cursor-pointer transition-colors ${
                  !selectedSubstituteUserId
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900">대직 담당자 없음</div>
                <div className="text-xs text-gray-500 mt-1">대직 담당자를 지정하지 않습니다.</div>
              </div>

              {/* 선택 가능한 대직 후보만 표시 (비매칭 행 제외) */}
              {(() => {
                const tableRows = availableSubstituteUsers
                  .filter((u) => u.id !== currentUser?.id)
                  .filter((u) => isSubstituteRowSelectable(u, currentUser))
                  .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));

                if (tableRows.length === 0) {
                  return (
                    <p className="text-sm text-gray-500 text-center py-4">
                      조건에 맞는 대직 후보가 없습니다.
                    </p>
                  );
                }

                return (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">이름</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">전화번호</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">부서</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">설계</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">청약</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">상태</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">선택</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tableRows.map((substituteUser) => {
                        const permissions = substituteUser.permissions || [];
                        const hasDesign = permissions.includes('DESIGN_RECEIVE_CREATE');
                        const hasSubscription = permissions.includes('SUBSCRIPTION_RECEIVE_CREATE');

                        // 원 사용자의 권한 확인
                        const selectedPermissions = currentUser?.permissions || [];
                        const selectedHasDesign = selectedPermissions.includes('DESIGN_RECEIVE_CREATE');
                        const selectedHasSubscription = selectedPermissions.includes('SUBSCRIPTION_RECEIVE_CREATE');

                        const isSelected = selectedSubstituteUserId === substituteUser.id;

                        return (
                          <tr
                            key={substituteUser.id}
                            onClick={() => {
                              onSelectSubstituteUser(substituteUser.id);
                              onClose();
                            }}
                            className={`transition-colors cursor-pointer ${
                              isSelected
                                ? 'bg-blue-50 hover:bg-blue-100'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {substituteUser.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 tabular-nums">
                              {formatPhoneMaskDigits4To7(substituteUser.phone)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {substituteUser.departmentName || '-'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {hasDesign ? (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    selectedHasDesign && hasDesign
                                      ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-300'
                                      : 'bg-blue-50 text-blue-600 opacity-70'
                                  }`}
                                  title={
                                    selectedHasDesign && hasDesign
                                      ? '내 설계 수신 권한과 일치'
                                      : '설계 수신 권한 보유(내 권한과 다름)'
                                  }
                                >
                                  설계
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {hasSubscription ? (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    selectedHasSubscription && hasSubscription
                                      ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300'
                                      : 'bg-emerald-50 text-emerald-700 opacity-70'
                                  }`}
                                  title={
                                    selectedHasSubscription && hasSubscription
                                      ? '내 청약 수신 권한과 일치'
                                      : '청약 수신 권한 보유(내 권한과 다름)'
                                  }
                                >
                                  청약
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                선택 가능
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {isSelected && (
                                <HiOutlineCheck className="w-5 h-5 text-blue-600 mx-auto" />
                              )}
                              {!isSelected && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectSubstituteUser(substituteUser.id);
                                    onClose();
                                  }}
                                  className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                                >
                                  선택
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubstituteUserSelection;
